import React, { useState, useEffect, forwardRef } from 'react';
import MaterialTable from 'material-table';
import { useMessage } from "../../context/message";
import { useAuth } from "../../context/auth";
import TextField from '@material-ui/core/TextField';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import CustomFilter from '../CustomFilter';
import { rangeFilter, dateRangeFilter } from '../FilterFunctions';
import RefreshIcon from '@material-ui/icons/Refresh';
import CircularProgress from '@material-ui/core/CircularProgress'
import CheckIcon from '@material-ui/icons/Check'
import CancelIcon from '@material-ui/icons/Cancel'
import AlertDialog from '../inputs/AlertDialog'
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom'
import InfoIcon from '@material-ui/icons/Info'
import InfoDialog from '../display/InfoDialog'

export default function MyReceivedBidsTable() {
  const { setMessage } = useMessage();
  const { auth } = useAuth();
  const [data, setData] = useState([]);
  const [toggle, fetchData] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false)
  const [openInfo, setOpenInfo] = useState(false)
  const [inquiry_bids_id, setInquiryBidsId] = useState(0)
  const [bounds, setBounds] = useState({});
  const [bid, setBid] = useState({
    inquiry_bids_id: '',
    reason: ''
  });
  const [accept, setAccept] = useState(false)

  const handleCloseInfo = () => {
    setOpenInfo(false)
    setInquiryBidsId(0)
  }

  const handleClose = () => {
    setBid({ inquiry_bids_id: '', reason: '' });
    setOpen(false);
  };

  useEffect(() => {
    fetch('/api/inquiry_bids/received', {
      method: 'GET',
      headers: {
        'x-access-token': sessionStorage.getItem('token')
      }
    })
    .then((response) => auth(response))
    .then((response) => {
      setData(response.data || []);
      setBounds(response.bounds || {});
    })
    .catch((error) => {
      setMessage({'type': 'error', 'text': 'Error! Unable to fetch received bids.'})
      console.error('Error:', error);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggle, setMessage]);

  const handleSubmit = () => {
    if(!loading){
      setLoading(true)
      fetch(`/api/inquiry_bids/${bid.inquiry_bids_id}/reject`, {
        method: 'DELETE',
        headers: {
          'x-access-token': sessionStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bid),
      })
      .then(response => auth(response))
      .then(response => {
        if (response.message.type === 'success') {
          handleClose();
        }
        setMessage(response.message);
        fetchData(!toggle);
        setLoading(false)
      })
      .catch(error => {
        setMessage({type: 'error', text: 'Error! Unable to reject bid'});
        setLoading(false)
        console.error('Error:', error);
      })
    }
  };

  const columns = [
    {
      title: 'Inquiry Id',
      field: 'inquiry_id',
      defaultGroupOrder: 0
    },
    {
      title: 'Inquiry Price (USD/BBL)',
      field: 'bid_price',
      type: 'numeric',
      bounds: bounds.bid_price,
      customFilterAndSearch: rangeFilter('bid_price'),
    },
    {
      title: 'Ask Price (USD/BBL)',
      field: 'ask_price',
      type: 'numeric',
      bounds: bounds.ask_price,
      customFilterAndSearch: rangeFilter('ask_price'),
    },
    {
      title: 'Start Date',
      field: 'date_available',
      type: 'date',
      customFilterAndSearch: dateRangeFilter('date_available'),
      customSort: (a,b) => {
        var date1 = new Date(a.date_available)
        var date2 = new Date(b.date_available)

        return date1 - date2
      },
      render: rowData => <p>{(new Date(rowData.date_available)).toLocaleDateString()}</p>
    },
    {
      title: 'Volume (MBBL)',
      field: 'volume',
      bounds: bounds.volume,
      customFilterAndSearch: rangeFilter('volume'),
    },
    {
      title: 'Comment',
      field: 'offer_comment',
      render: rowData => (
        rowData.offer_comment && (rowData.offer_comment.length > 30 ? rowData.offer_comment.substring(0,30) + '...' : rowData.offer_comment)
      )
    },
    {
      title: 'Allow Sublet',
      field: 'allow_sublet',
      lookup: {
        0: 'N',
        1: 'Y'
      }
    }
  ];

  const acceptBid = () => {
    fetch(`/api/inquiry_booking/${inquiry_bids_id}`,{
      method: 'POST',
      headers: {
        'x-access-token': sessionStorage.getItem('token')
      }
    })
    .then((response) => auth(response))
    .then(response => {
      if (response.message.type === 'success'){
        fetchData(!toggle);
      }
      setMessage(response.message);
    })
    .catch((error) => {
      setMessage({'type': 'error', 'text': 'Error! Unable to accept bid.'})
      console.error('Error:', error)
    })
  }

  return (
    <div>
      <InfoDialog
        open = {openInfo}
        onClose = {handleCloseInfo}
        type = 'inquiry'
        id = {inquiry_bids_id}
      />
      <AlertDialog
        open = {accept}
        setOpen = {setAccept}
        title = 'Negotiate'
        content = 'By clicking negotiate you are agreeing to reveal your contact information to the offerer to finish negotiations'
        submitText = 'Negotiate'
        submit = {acceptBid}
      />
      <MaterialTable
        title = 'Offers on Your Inquiries'
        columns = {columns}
        data = {data}
        icons={{Export: forwardRef((props, ref) => <VerticalAlignBottomIcon color = 'primary' {...props} ref={ref}/>)}}
        options = {{
          filtering: true,
          draggable: false,
          actionsColumnIndex: 0,
          grouping: true
        }}
        components = {{
          Groupbar: () => null,
          FilterRow: props => <CustomFilter {...props} />
        }}
        actions = {[
          {
            icon: () => <RefreshIcon color = 'primary'/>,
            tooltip: 'Refresh',
            isFreeAction: true,
            onClick: (event) => fetchData(!toggle)
          },
          {
            icon : () => <CheckIcon color = 'primary'/>,
            tooltip : 'Negotiate',
            onClick: (event, rowData) => {
              setInquiryBidsId(rowData.inquiry_bids_id)
              setAccept(true)
            }
          },
          {
            icon : () => <CancelIcon color = 'primary'/>,
            tooltip : 'Reject Offer',
            onClick: (event, rowData) => {
              setBid({ ...bid, inquiry_bids_id: rowData.inquiry_bids_id });
              setOpen(true);
            }
          },
          {
            icon: () => <InfoIcon color = 'primary'/>,
            tooltip: 'More Info',
            onClick: (e, rowData) => {
              setInquiryBidsId(rowData.inquiry_bids_id)
              setOpenInfo(true)
            }
          }
        ]}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth='sm'
        fullWidth={true}
        >
          <DialogTitle>Reject Offer</DialogTitle>
          <DialogContent>
            <TextField
              required
              multiline
              fullWidth
              name='reason'
              label='Reason'
              value={bid.reason}
              onChange={e => setBid({...bid, reason: e.target.value})}
            />
          </DialogContent>
          <DialogActions>
            <div style = {{position: 'relative'}}>
              <Button onClick={handleSubmit} color="primary">
                Reject
              </Button>
              {loading &&
                <CircularProgress size={32} style = {{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: -16,
                  marginLeft: -16
                  }}
                />
              }
            </div>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
    </div>
  );
}
